import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FlaskConical, ArrowRight, BarChart3 } from 'lucide-react';
import Sidebar from '../../components/sidebar/Sidebar';
import { ResponsiveAppShell, TaskyTopBar, TaskyButton, TaskyTextarea } from '../../components/tasky';
import { evaluateJev, JevChoice, JevComparisonInput, JevComparisonResult } from '../../services/api/jevService';

const EMPTY_INPUT: JevComparisonInput = { criterion: '', context: '', option_a: '', option_b: '' };
const EXAMPLE: JevComparisonInput = {
  criterion: '初めて読む人に、商品の用途とメリットが具体的に伝わるのはどちらか。',
  context: '架空の商品：チーム向けのタスク管理アプリ。担当者・締め切り・進捗を一覧で共有できる。',
  option_a: '仕事をもっとスマートに。未来の働き方を、あなたに。',
  option_b: '担当者・締め切り・進捗をひと目で共有。チームのタスク管理をひとつの画面に。',
};
const LABELS: Record<JevChoice, string> = { a: 'A案', b: 'B案', tie: '同程度', insufficient: '判断材料が不足' };
const OUTCOMES: Record<JevChoice, string> = {
  a: '評価基準により合うのは A案',
  b: '評価基準により合うのは B案',
  tie: 'この評価基準では同程度です',
  insufficient: '比較するための判断材料が不足しています',
};
const CHOICES: JevChoice[] = ['a', 'b', 'tie', 'insufficient'];
const percent = (value: number) => `${(value * 100).toFixed(1)}%`;

function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    switch (error.response?.status) {
      case 401: return 'ログイン状態を確認して、もう一度お試しください。';
      case 403: return '所属組織や利用プランの状態を確認してください。';
      case 422: return '入力内容と文字数を確認してください。評価基準・A案・B案は必須です。';
      case 429: return '短時間に評価が集中しています。1分ほど待ってからお試しください。';
      case 503: return 'この環境ではJevを利用できません。管理者に設定をご確認ください。';
      default: break;
    }
  }
  return '評価結果を取得できませんでした。入力内容を残していますので、時間をおいて再度お試しください。';
}

const JevEvaluationPage: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [input, setInput] = useState<JevComparisonInput>(EMPTY_INPUT);
  const [result, setResult] = useState<JevComparisonResult | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => pending.current?.abort(), []);

  const update = (key: keyof JevComparisonInput, value: string) => {
    setInput(previous => ({ ...previous, [key]: value }));
    setResult(null);
    setError('');
  };
  const canSubmit = Boolean(input.criterion.trim() && input.option_a.trim() && input.option_b.trim());
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || pending.current) return;
    const controller = new AbortController();
    pending.current = controller;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const response = await evaluateJev({
        criterion: input.criterion.trim(), context: input.context.trim(),
        option_a: input.option_a.trim(), option_b: input.option_b.trim(),
      }, controller.signal);
      if (!controller.signal.aborted) setResult(response);
    } catch (failure) {
      if (!controller.signal.aborted) setError(errorMessage(failure));
    } finally {
      if (!controller.signal.aborted) {
        pending.current = null;
        setLoading(false);
      }
    }
  };

  return (
    <ResponsiveAppShell
      sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
      mobileSidebar={<Sidebar />}
      topbarTitle="Jev評価"
    >
      <div className="flex min-h-[calc(100dvh_-_56px)] flex-col lg:h-screen lg:min-h-0">
        <TaskyTopBar title="Jev評価" icon={<FlaskConical className="h-5 w-5" />}
          sidebarCollapsed={sidebarCollapsed} onExpandSidebar={() => setSidebarCollapsed(false)} />
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="mx-auto max-w-5xl space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="mb-2 text-xs font-semibold tracking-wider text-primary">Jevのお試し</p>
                <h2 className="text-2xl font-semibold text-dark">2つの案を、同じ基準で比べる</h2>
                <p className="mt-2 text-sm leading-6 text-gray-600">広告コピーや企画の文章を入力すると、Jevが評価基準に沿って比較します。</p>
              </div>
              <TaskyButton variant="secondary" size="sm" disabled={loading} onClick={() => {
                setInput(EXAMPLE); setResult(null); setError('');
              }}>サンプルを入力</TaskyButton>
            </div>

            <form onSubmit={submit} className="space-y-5" aria-label="Jev比較フォーム">
              <fieldset disabled={loading} className="space-y-5">
                <div className="rounded-xl border border-warm-border bg-white p-4 sm:p-6">
                  <label htmlFor="jev-criterion" className="mb-2 block text-sm font-semibold text-dark">評価基準 <span className="font-normal text-gray-500">必須</span></label>
                  <TaskyTextarea id="jev-criterion" required maxLength={1000} rows={2} value={input.criterion}
                    onChange={event => update('criterion', event.target.value)}
                    placeholder="例：初めて読む人に、商品のメリットが具体的に伝わるのはどちらか" />
                  <label htmlFor="jev-context" className="mb-2 mt-5 block text-sm font-semibold text-dark">背景・ターゲット <span className="font-normal text-gray-500">任意</span></label>
                  <TaskyTextarea id="jev-context" maxLength={6000} rows={3} value={input.context}
                    onChange={event => update('context', event.target.value)}
                    placeholder="商品の特徴、届けたい相手、判断の根拠になる情報など" />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {(['option_a', 'option_b'] as const).map((key, index) => (
                    <div key={key} className="min-w-0 rounded-xl border border-warm-border bg-white p-4 sm:p-6">
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <label htmlFor={`jev-${key}`} className="text-sm font-semibold text-dark">{index === 0 ? 'A案' : 'B案'} <span className="font-normal text-gray-500">必須</span></label>
                        <span className="text-xs tabular-nums text-gray-400">{input[key].length.toLocaleString()} / 4,000</span>
                      </div>
                      <TaskyTextarea id={`jev-${key}`} required maxLength={4000} rows={6} value={input[key]}
                        onChange={event => update(key, event.target.value)} placeholder={`比較したい${index === 0 ? 'A' : 'B'}案の文章を入力`} />
                    </div>
                  ))}
                </div>
              </fieldset>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="max-w-xl text-xs leading-5 text-gray-500">表示する確率はモデルの判断であり、実際のABテストの勝率・CTR・正解率を示すものではありません。</p>
                <TaskyButton type="submit" disabled={!canSubmit} isLoading={loading}>
                  {loading ? 'Jevで評価中…' : 'Jevで評価する'}{!loading && <ArrowRight />}
                </TaskyButton>
              </div>
            </form>

            <div aria-live="polite" aria-busy={loading}>
              {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</p>}
              {result ? (
                <section aria-label="評価結果" className="rounded-xl border border-warm-border bg-white p-4 sm:p-6">
                  <div className="mb-5 flex items-center gap-2 text-primary"><BarChart3 className="h-5 w-5" /><h3 className="text-sm font-semibold">評価結果</h3></div>
                  <p className="text-xl font-semibold text-dark">{OUTCOMES[result.choice]}</p>
                  {result.choice === 'insufficient' && <p className="mt-2 text-sm text-gray-600">評価基準や背景情報を具体的にして、もう一度お試しください。</p>}
                  <p className="mb-4 mt-5 text-xs text-gray-500">各判定に割り当てられた確率</p>
                  <div className="space-y-4">
                    {CHOICES.map(choice => (
                      <div key={choice}>
                        <div className="mb-1.5 flex justify-between gap-3 text-sm"><span className={result.choice === choice ? 'font-semibold text-primary' : 'text-gray-600'}>{LABELS[choice]}</span><span className="tabular-nums text-dark">{percent(result.probabilities[choice])}</span></div>
                        <div role="meter" aria-label={`${LABELS[choice]}の確率`} aria-valuenow={result.probabilities[choice] * 100} aria-valuemin={0} aria-valuemax={100} className="h-2 overflow-hidden rounded-full bg-warm-surface">
                          <div className={`h-full rounded-full ${result.choice === choice ? 'bg-primary' : 'bg-gray-300'}`} style={{ width: percent(result.probabilities[choice]) }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 border-t border-warm-border pt-4 text-xs text-gray-500">
                    {result.confidence !== null && <span>モデルの確信度（confidence）：{percent(result.confidence)}</span>}
                    <span>評価時間：{(result.elapsed_ms / 1000).toFixed(2)}秒</span>
                    <span>入力：{result.input_tokens.toLocaleString()}トークン</span>
                  </div>
                  <p className="mt-3 text-xs leading-5 text-gray-500">確信度は上の確率とは別の指標です。いずれも、正解データで測定した精度ではありません。</p>
                </section>
              ) : !error && (
                <div className="rounded-xl border border-dashed border-warm-border p-8 text-center text-sm text-gray-500">
                  {loading ? '入力した2案を比較しています。' : '評価すると、比較結果と確率がここに表示されます。'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ResponsiveAppShell>
  );
};

export default JevEvaluationPage;
